import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase.config';

export interface Card {
  id: string;
  sides: Array<{
    name: string;
    content: string;
  }>;
  isLearned?: boolean;
  difficulty?: number;
  easyStreak?: number;
  isStarred?: boolean;
  lastShown?: string;
  nextReview?: string | null;
  createdAt?: number;
}

export interface DeckStats {
  totalCards: number;
  cardsLearned: number;
  cardsStudied: number;
  averageRating: number;
  lastStudied: string;
  dailyProgress: Array<{
    date: string;
    cards: number;
  }>;
  popularity?: number; // Количество пользователей, добавивших колоду
}

export interface Deck {
  id: string;
  title: string;
  description: string;
  sideNames: string[];
  tags: string[];
  userId: string;
  stats: DeckStats;
  isPublic?: boolean; // Флаг, указывающий, является ли колода публичной
  fromClassId?: string;  // ID класса, из которого была добавлена колода
  fromClassName?: string; // Название класса, из которого была добавлена колода
}

export const getDeck = async (deckId: string): Promise<Deck | null> => {
  try {
    const deckRef = doc(db, 'decks', deckId);
    const deckDoc = await getDoc(deckRef);
    
    if (deckDoc.exists()) {
      const data = deckDoc.data();
      return {
        id: deckDoc.id,
        title: data.title,
        description: data.description,
        sideNames: data.sideNames,
        tags: data.tags,
        userId: data.userId,
        isPublic: data.isPublic || false,
        fromClassId: data.fromClassId,
        fromClassName: data.fromClassName,
        stats: {
          totalCards: data.stats.totalCards || 0,
          cardsLearned: data.stats.cardsLearned || 0,
          cardsStudied: data.stats.cardsStudied || 0,
          averageRating: data.stats.averageRating || 0,
          lastStudied: data.stats.lastStudied || new Date().toISOString(),
          dailyProgress: data.stats.dailyProgress || [],
          popularity: data.stats.popularity || 0
        }
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching deck:', error);
    return null;
  }
};

export const getDeckById = getDeck;

export const createDeck = async (deckData: {
  title: string;
  description: string;
  sideNames: string[];
  tags: string[];
  userId: string;
  isPublic?: boolean;
}) => {
  try {
    const decksRef = collection(db, 'decks');
    const newDeck = {
      ...deckData,
      isPublic: deckData.isPublic || false, // По умолчанию колода приватная
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      stats: {
        totalCards: 0,
        cardsLearned: 0,
        averageRating: 0,
        lastStudied: null,
        dailyProgress: [],
        popularity: 0
      },
    };
    const docRef = await addDoc(decksRef, newDeck);
    return { id: docRef.id, ...newDeck };
  } catch (error) {
    console.error('Error creating deck:', error);
    return null;
  }
};

export const updateDeck = async (
  deckId: string,
  deckData: {
    title?: string;
    description?: string;
    sideNames?: string[];
    tags?: string[];
    isPublic?: boolean;
  }
) => {
  try {
    const deckRef = doc(db, 'decks', deckId);
    await updateDoc(deckRef, {
      ...deckData,
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Error updating deck:', error);
    return false;
  }
};

export const deleteDeck = async (deckId: string) => {
  try {
    const deckRef = doc(db, 'decks', deckId);
    await deleteDoc(deckRef);
    return true;
  } catch (error) {
    console.error('Error deleting deck:', error);
    return false;
  }
};

export const getCards = async (deckId: string): Promise<Card[]> => {
  try {
    const cardsRef = collection(db, 'decks', deckId, 'cards');
    const cardsSnapshot = await getDocs(cardsRef);
    
    const cards = cardsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        sides: Array.isArray(data.sides) ? data.sides.map((side: any) => ({
          name: side.name || `Side ${data.sides.indexOf(side) + 1}`,
          content: side.content
        })) : [],
        isLearned: data.isLearned || false,
        difficulty: data.difficulty || 0,
        easyStreak: data.easyStreak || 0,
        isStarred: data.isStarred || false,
        createdAt: data.createdAt || Date.now(),
        lastShown: data.lastShown || null,
        nextReview: data.nextReview || null
      };
    });

    return cards.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  } catch (error) {
    console.error('Error getting cards:', error);
    return [];
  }
};

export const addCard = async (deckId: string, sides: string[]) => {
  try {
    const cardsRef = collection(db, 'decks', deckId, 'cards');
    const newCard = {
      sides: sides.map((content, index) => ({
        name: `Side ${index + 1}`,
        content: content
      })),
      createdAt: Date.now(),
      updatedAt: new Date().toISOString(),
      isLearned: false,
      difficulty: 0,
      easyStreak: 0,
      isStarred: false
    };
    const docRef = await addDoc(cardsRef, newCard);
    return { id: docRef.id, ...newCard };
  } catch (error) {
    console.error('Error adding card:', error);
    return null;
  }
};

export const updateCard = async (
  deckId: string,
  cardId: string,
  sides: string[],
  updates?: Partial<Card>
): Promise<boolean> => {
  try {
    const cardRef = doc(db, 'decks', deckId, 'cards', cardId);
    const cardData = {
      sides: sides.map((content, index) => ({
        name: `Side ${index + 1}`,
        content
      })),
      ...updates
    };
    await updateDoc(cardRef, cardData);
    return true;
  } catch (error) {
    console.error('Error updating card:', error);
    return false;
  }
};

export const deleteCard = async (deckId: string, cardId: string) => {
  try {
    const cardRef = doc(db, 'decks', deckId, 'cards', cardId);
    await deleteDoc(cardRef);
    return true;
  } catch (error) {
    console.error('Error deleting card:', error);
    return false;
  }
};

export const getUserDecks = async (userId: string): Promise<Deck[]> => {
  try {
    console.log("[getUserDecks] Requesting decks for user:", userId);
    
    // Получаем колоды, созданные пользователем
    const decksRef = collection(db, 'decks');
    const q = query(decksRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const ownDecks = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        description: data.description,
        sideNames: data.sideNames,
        tags: data.tags,
        userId: data.userId,
        isPublic: data.isPublic || false,
        stats: {
          totalCards: data.stats.totalCards || 0,
          cardsLearned: data.stats.cardsLearned || 0,
          cardsStudied: data.stats.cardsStudied || 0,
          averageRating: data.stats.averageRating || 0,
          lastStudied: data.stats.lastStudied || new Date().toISOString(),
          dailyProgress: data.stats.dailyProgress || [],
          popularity: data.stats.popularity || 0
        }
      };
    });
    console.log("[getUserDecks] Found own decks:", ownDecks.length);

    // Получаем колоды, добавленные пользователю (из классов)
    const userDecksRef = collection(db, 'userDecks', userId, 'decks');
    const userDecksSnapshot = await getDocs(userDecksRef);
    
    const sharedDecks = userDecksSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        description: data.description,
        sideNames: data.sideNames || [],
        tags: data.tags || [],
        userId: data.userId,
        isPublic: data.isPublic || false,
        fromClassId: data.fromClassId,
        fromClassName: data.fromClassName,
        stats: {
          totalCards: data.stats?.totalCards || 0,
          cardsLearned: data.stats?.cardsLearned || 0,
          cardsStudied: data.stats?.cardsStudied || 0,
          averageRating: data.stats?.averageRating || 0,
          lastStudied: data.stats?.lastStudied || new Date().toISOString(),
          dailyProgress: data.stats?.dailyProgress || [],
          popularity: data.stats?.popularity || 0
        }
      };
    });
    console.log("[getUserDecks] Found shared decks from classes:", sharedDecks.length);
    
    // Объединяем результаты и убираем возможные дубликаты
    const allDecks = [...ownDecks];
    
    // Добавляем только те колоды из userDecks, которых нет в основных колодах
    for (const sharedDeck of sharedDecks) {
      if (!allDecks.some(deck => deck.id === sharedDeck.id)) {
        allDecks.push(sharedDeck);
      }
    }
    
    console.log("[getUserDecks] Total decks:", allDecks.length);
    return allDecks;
  } catch (error) {
    console.error('Error fetching user decks:', error);
    return [];
  }
};

export const addUserDeck = async (userId: string, deckData: Deck) => {
  try {
    console.log("[addUserDeck] Adding deck to user:", userId, "Deck:", deckData.id, deckData.title);
    
    const userDecksRef = collection(db, 'userDecks', userId, 'decks');
    
    const q = query(userDecksRef, where('id', '==', deckData.id));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      console.log("[addUserDeck] Deck already exists in user collection");
      return true;
    }
    
    console.log("[addUserDeck] Adding new deck to user collection");
    await setDoc(doc(userDecksRef, deckData.id), {
      ...deckData,
      addedAt: new Date().toISOString()
    });
    
    console.log("[addUserDeck] Deck successfully added");
    return true;
  } catch (error) {
    console.error('[addUserDeck] Error adding deck to user collection:', error);
    return false;
  }
};

export const removeUserDeck = async (userId: string, deckId: string) => {
  try {
    const userDeckRef = doc(db, 'userDecks', userId, 'decks', deckId);
    
    await deleteDoc(userDeckRef);
    
    return true;
  } catch (error) {
    console.error('Error removing deck from user collection:', error);
    return false;
  }
};

export const updateCardProgress = async (deckId: string, cardId: string, response: 'learned' | 'easy' | 'hard', isStarred: boolean) => {
  try {
    const cardRef = doc(db, 'decks', deckId, 'cards', cardId);
    const cardDoc = await getDoc(cardRef);
    const cardData = cardDoc.data() as Card;

    const now = new Date();
    let updates: Partial<Card> = {
      lastShown: now.toISOString(),
      isStarred,
    };

    if (response === 'learned') {
      updates.isLearned = true;
      updates.difficulty = 0;
      updates.easyStreak = 0;
      updates.nextReview = undefined;
    } else if (response === 'easy') {
      const newStreak = (cardData.easyStreak || 0) + 1;
      updates.easyStreak = newStreak;
      updates.difficulty = 1;
      
      if (newStreak >= 3) {
        updates.isLearned = true;
        updates.nextReview = undefined;
      } else {
        const lastShownDate = cardData.lastShown ? new Date(cardData.lastShown) : now;
        const nextReviewDate = cardData.nextReview ? new Date(cardData.nextReview) : new Date(now.getTime() + 24 * 60 * 60 * 1000);
        const currentInterval = nextReviewDate.getTime() - lastShownDate.getTime();
        updates.nextReview = new Date(now.getTime() + currentInterval * 2).toISOString();
      }
    } else if (response === 'hard') {
      updates.difficulty = -1;
      updates.easyStreak = 0;
      const lastShownDate = cardData.lastShown ? new Date(cardData.lastShown) : now;
      const nextReviewDate = cardData.nextReview ? new Date(cardData.nextReview) : new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const currentInterval = nextReviewDate.getTime() - lastShownDate.getTime();
      updates.nextReview = new Date(now.getTime() + currentInterval / 2).toISOString();
    }

    await updateDoc(cardRef, updates);
    return true;
  } catch (error) {
    console.error('Error updating card progress:', error);
    return false;
  }
};

export const updateDeckStats = async (deckId: string) => {
  try {
    const cardsRef = collection(db, 'decks', deckId, 'cards');
    const cardsSnapshot = await getDocs(cardsRef);
    const cards = cardsSnapshot.docs.map(doc => doc.data() as Card);

    const totalCards = cards.length;
    const cardsLearned = cards.filter(card => card.isLearned).length;
    const cardsStudied = cards.filter(card => card.lastShown).length;
    console.log("cardsLearned:" + cardsLearned)
    console.log("cardsStudied:" + cardsStudied)

    const deckRef = doc(db, 'decks', deckId);
    await updateDoc(deckRef, {
      'stats.totalCards': totalCards,
      'stats.cardsLearned': cardsLearned,
      'stats.cardsStudied': cardsStudied,
      'stats.lastStudied': new Date().toISOString()
    });

    return true;
  } catch (error) {
    console.error('Error updating deck stats:', error);
    return false;
  }
};

// Get all cards that a specific user has starred in a deck
export const getUserStarredCards = async (userId: string, deckId: string): Promise<string[]> => {
  try {
    if (!userId || !deckId) {
      console.warn('Invalid userId or deckId provided to getUserStarredCards');
      return [];
    }

    // Check if user progress document exists
    const userProgressRef = doc(db, 'userProgress', `${userId}_${deckId}`);
    const userProgressDoc = await getDoc(userProgressRef);
    
    if (userProgressDoc.exists()) {
      const data = userProgressDoc.data();
      return data.starredCards || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting user starred cards:', error);
    return [];
  }
};

// Toggle star status for a specific card
export const toggleCardStar = async (userId: string, deckId: string, cardId: string): Promise<boolean> => {
  try {
    if (!userId || !deckId || !cardId) {
      console.warn('Invalid parameters provided to toggleCardStar');
      return false;
    }

    // Reference to the user progress document
    const userProgressRef = doc(db, 'userProgress', `${userId}_${deckId}`);
    const userProgressDoc = await getDoc(userProgressRef);
    
    if (userProgressDoc.exists()) {
      const data = userProgressDoc.data();
      const starredCards = Array.isArray(data.starredCards) ? data.starredCards : [];
      
      // Check if the card is already starred
      const cardIndex = starredCards.indexOf(cardId);
      
      if (cardIndex > -1) {
        // Remove the card from starred list
        starredCards.splice(cardIndex, 1);
      } else {
        // Add the card to starred list
        starredCards.push(cardId);
      }
      
      // Update the document
      await updateDoc(userProgressRef, {
        starredCards: starredCards
      });
    } else {
      // Create a new document if it doesn't exist
      await setDoc(userProgressRef, {
        userId: userId,
        deckId: deckId,
        cardsLearned: 0,
        totalCards: 0,
        lastStudied: new Date().toISOString(),
        starredCards: [cardId]
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error toggling card star:', error);
    return false;
  }
};

// Update user's progress for a deck
export const updateUserProgress = async (userId: string, deckId: string, cardsLearned: number, totalCards: number): Promise<boolean> => {
  try {
    if (!userId || !deckId) {
      console.warn('Invalid parameters provided to updateUserProgress');
      return false;
    }

    // Reference to the user progress document
    const userProgressRef = doc(db, 'userProgress', `${userId}_${deckId}`);
    const userProgressDoc = await getDoc(userProgressRef);
    
    if (userProgressDoc.exists()) {
      // Update existing document
      await updateDoc(userProgressRef, {
        cardsLearned: cardsLearned,
        totalCards: totalCards,
        lastStudied: new Date().toISOString()
      });
    } else {
      // Create a new document if it doesn't exist
      await setDoc(userProgressRef, {
        userId: userId,
        deckId: deckId,
        cardsLearned: cardsLearned,
        totalCards: totalCards,
        lastStudied: new Date().toISOString(),
        starredCards: []
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating user progress:', error);
    return false;
  }
};

// Get user's progress for a specific deck
export const getUserProgressForDeck = async (userId: string, deckId: string): Promise<{
  cardsLearned: number;
  totalCards: number;
  lastStudied: Date | null;
  progress: number;
} | null> => {
  try {
    if (!userId || !deckId) {
      console.warn('Invalid parameters provided to getUserProgressForDeck');
      return null;
    }

    // Reference to the user progress document
    const userProgressRef = doc(db, 'userProgress', `${userId}_${deckId}`);
    const userProgressDoc = await getDoc(userProgressRef);
    
    if (userProgressDoc.exists()) {
      const data = userProgressDoc.data();
      const cardsLearned = data.cardsLearned || 0;
      const totalCards = data.totalCards || 0;
      
      // Calculate progress percentage
      const progress = totalCards > 0 ? Math.round((cardsLearned / totalCards) * 100) : 0;
      
      return {
        cardsLearned,
        totalCards,
        lastStudied: data.lastStudied ? new Date(data.lastStudied) : null,
        progress
      };
    }
    
    return {
      cardsLearned: 0,
      totalCards: 0,
      lastStudied: null,
      progress: 0
    };
  } catch (error) {
    console.error('Error getting user progress for deck:', error);
    return null;
  }
};

// Получение публичных колод
export const getPublicDecks = async (limit: number = 20): Promise<Deck[]> => {
  try {
    const decksRef = collection(db, 'decks');
    const q = query(decksRef, where('isPublic', '==', true));
    const decksSnapshot = await getDocs(q);
    
    const decks: Deck[] = [];
    
    for (const deckDoc of decksSnapshot.docs) {
      const data = deckDoc.data();
      decks.push({
        id: deckDoc.id,
        title: data.title,
        description: data.description,
        sideNames: data.sideNames,
        tags: data.tags,
        userId: data.userId,
        isPublic: data.isPublic,
        stats: {
          totalCards: data.stats.totalCards || 0,
          cardsLearned: data.stats.cardsLearned || 0,
          cardsStudied: data.stats.cardsStudied || 0,
          averageRating: data.stats.averageRating || 0,
          lastStudied: data.stats.lastStudied || new Date().toISOString(),
          dailyProgress: data.stats.dailyProgress || [],
          popularity: data.stats.popularity || 0
        }
      });
    }
    
    return decks.slice(0, limit);
  } catch (error) {
    console.error('Error fetching public decks:', error);
    return [];
  }
};

// Поиск публичных колод по заданному запросу
export const searchPublicDecks = async (
  searchTerm: string,
  searchInTags: boolean = true,
  searchInTitle: boolean = true,
  searchInDescription: boolean = true,
  limit: number = 20
): Promise<Deck[]> => {
  try {
    // Получаем все публичные колоды
    const decksRef = collection(db, 'decks');
    const q = query(decksRef, where('isPublic', '==', true));
    const decksSnapshot = await getDocs(q);
    
    const searchTermLower = searchTerm.toLowerCase();
    const decks: Deck[] = [];
    
    for (const deckDoc of decksSnapshot.docs) {
      const data = deckDoc.data();
      let isMatch = false;
      
      // Поиск в тегах
      if (searchInTags && Array.isArray(data.tags)) {
        isMatch = data.tags.some((tag: string) => 
          tag.toLowerCase().includes(searchTermLower)
        );
      }
      
      // Поиск в названии
      if (!isMatch && searchInTitle && data.title) {
        isMatch = data.title.toLowerCase().includes(searchTermLower);
      }
      
      // Поиск в описании
      if (!isMatch && searchInDescription && data.description) {
        isMatch = data.description.toLowerCase().includes(searchTermLower);
      }
      
      if (isMatch) {
        decks.push({
          id: deckDoc.id,
          title: data.title,
          description: data.description,
          sideNames: data.sideNames,
          tags: data.tags,
          userId: data.userId,
          isPublic: data.isPublic,
          stats: {
            totalCards: data.stats.totalCards || 0,
            cardsLearned: data.stats.cardsLearned || 0,
            cardsStudied: data.stats.cardsStudied || 0,
            averageRating: data.stats.averageRating || 0,
            lastStudied: data.stats.lastStudied || new Date().toISOString(),
            dailyProgress: data.stats.dailyProgress || [],
            popularity: data.stats.popularity || 0
          }
        });
      }
    }
    
    return decks.slice(0, limit);
  } catch (error) {
    console.error('Error searching public decks:', error);
    return [];
  }
};

// Получение самых популярных публичных колод
export const getPopularPublicDecks = async (limit: number = 10): Promise<Deck[]> => {
  try {
    const decksRef = collection(db, 'decks');
    const q = query(decksRef, where('isPublic', '==', true));
    const decksSnapshot = await getDocs(q);
    
    const decks: Deck[] = [];
    
    for (const deckDoc of decksSnapshot.docs) {
      const data = deckDoc.data();
      decks.push({
        id: deckDoc.id,
        title: data.title,
        description: data.description,
        sideNames: data.sideNames,
        tags: data.tags,
        userId: data.userId,
        isPublic: data.isPublic,
        stats: {
          totalCards: data.stats.totalCards || 0,
          cardsLearned: data.stats.cardsLearned || 0,
          cardsStudied: data.stats.cardsStudied || 0,
          averageRating: data.stats.averageRating || 0,
          lastStudied: data.stats.lastStudied || new Date().toISOString(),
          dailyProgress: data.stats.dailyProgress || [],
          popularity: data.stats.popularity || 0
        }
      });
    }
    
    // Сортируем по популярности (количеству пользователей, добавивших колоду)
    decks.sort((a, b) => (b.stats.popularity || 0) - (a.stats.popularity || 0));
    
    return decks.slice(0, limit);
  } catch (error) {
    console.error('Error fetching popular decks:', error);
    return [];
  }
};

// Проверка, есть ли колода уже в библиотеке пользователя
export const isDeckInUserLibrary = async (userId: string, deckId: string): Promise<boolean> => {
  try {
    if (!userId || !deckId) {
      return false;
    }
    
    // Проверяем колоды пользователя
    const decksRef = collection(db, 'decks');
    const q = query(decksRef, where('userId', '==', userId), where('id', '==', deckId));
    const ownDecksSnapshot = await getDocs(q);
    
    // Если это колода, созданная самим пользователем
    if (!ownDecksSnapshot.empty) {
      return true;
    }
    
    // Проверяем добавленные колоды в библиотеке пользователя
    const userDecksRef = collection(db, 'userDecks', userId, 'decks');
    const docRef = doc(userDecksRef, deckId);
    const deckDoc = await getDoc(docRef);
    
    return deckDoc.exists();
  } catch (error) {
    console.error('Error checking if deck is in user library:', error);
    return false;
  }
};

// Добавление публичной колоды в библиотеку пользователя
export const addPublicDeckToUser = async (userId: string, deckId: string): Promise<boolean> => {
  try {
    // Получаем информацию о колоде
    const deck = await getDeck(deckId);
    
    if (!deck) {
      console.error('Deck not found');
      return false;
    }
    
    if (!deck.isPublic) {
      console.error('This deck is not public');
      return false;
    }
    
    // Проверяем, есть ли колода уже в библиотеке
    const isInLibrary = await isDeckInUserLibrary(userId, deckId);
    
    // Если колода уже в библиотеке пользователя, проверяем, это колода класса или нет
    if (isInLibrary) {
      // Проверяем, есть ли колода в библиотеке как часть класса
      const userDecksRef = collection(db, 'userDecks', userId, 'decks');
      const docRef = doc(userDecksRef, deckId);
      const deckDoc = await getDoc(docRef);
      
      if (deckDoc.exists()) {
        const data = deckDoc.data();
        
        // Если колода привязана к классу, обновляем ее и удаляем привязку
        if (data.fromClassId) {
          await updateDoc(docRef, {
            fromClassId: null,
            fromClassName: null
          });
          console.log('Updated deck from class to regular public deck in user library');
          return true;
        }
      }
      
      console.log('Deck is already in user library and is not from a class');
      return false;
    }
    
    // Добавляем колоду пользователю
    const userDecksRef = collection(db, 'userDecks', userId, 'decks');
    await setDoc(doc(userDecksRef, deckId), {
      addedAt: new Date().toISOString(),
      deckId: deckId,
      userId: deck.userId,
      title: deck.title,
      description: deck.description,
      sideNames: deck.sideNames,
      tags: deck.tags,
      isPublic: deck.isPublic,
      stats: deck.stats
    });
    
    // Пытаемся увеличить счетчик популярности колоды в отдельной операции
    // Если это не удастся из-за прав доступа, все равно считаем операцию успешной
    try {
      const deckRef = doc(db, 'decks', deckId);
      const deckDoc = await getDoc(deckRef);
      if (deckDoc.exists()) {
        const data = deckDoc.data();
        const currentPopularity = data.stats?.popularity || 0;
        
        await updateDoc(deckRef, {
          'stats.popularity': currentPopularity + 1
        });
      }
    } catch (popError) {
      console.warn('Could not update deck popularity, but deck was added to library:', popError);
      // Игнорируем ошибку, так как колода уже добавлена в библиотеку
    }
    
    return true;
  } catch (error) {
    console.error('Error adding public deck to user:', error);
    return false;
  }
};

// Функция для обновления флага isPublic для колод из класса
export const updateClassDeckPublicFlag = async (userId: string, deckId: string): Promise<boolean> => {
  try {
    // Получаем оригинальную колоду
    const originalDeck = await getDeck(deckId);
    if (!originalDeck) {
      console.error(`Original deck ${deckId} not found`);
      return false;
    }

    // Получаем колоду из библиотеки пользователя
    const userDecksRef = collection(db, 'userDecks', userId, 'decks');
    const docRef = doc(userDecksRef, deckId);
    const deckDoc = await getDoc(docRef);
    
    if (!deckDoc.exists()) {
      console.error(`Deck ${deckId} not found in user library`);
      return false;
    }
    
    // Проверяем, отличается ли флаг публичности
    const userData = deckDoc.data();
    if (userData.isPublic !== originalDeck.isPublic) {
      // Обновляем флаг публичности
      await updateDoc(docRef, {
        isPublic: originalDeck.isPublic
      });
      console.log(`Updated isPublic flag for deck ${deckId} in user ${userId} library to ${originalDeck.isPublic}`);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error(`Error updating isPublic flag for deck ${deckId}:`, error);
    return false;
  }
}; 